import { useEffect, useState } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Bot,
  CalendarCheck2,
  CheckCircle2,
  Flame,
  Map,
  Sparkles,
  Target,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../../firebase";

const Section = styled.section`
  position: relative;
  padding: 104px 20px 132px;
  overflow: hidden;
  background: radial-gradient(
      720px at 12% 14%,
      rgba(245, 158, 11, 0.18),
      transparent 62%
    ),
    radial-gradient(760px at 82% 18%, rgba(14, 165, 233, 0.2), transparent 64%),
    radial-gradient(920px at 50% 100%, rgba(34, 197, 94, 0.08), transparent 68%),
    ${({ theme }) => theme.colors.bg};
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
`;

const HeroGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.03fr) minmax(340px, 0.97fr);
  gap: 56px;
  align-items: center;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
    gap: 42px;
  }
`;

const Left = styled.div`
  max-width: 620px;
`;

const Eyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.56);
  border: 1px solid rgba(148, 163, 184, 0.2);
  color: ${({ theme }) => theme.colors.text};
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 0.02em;
`;

const Heading = styled.h1`
  margin: 24px 0 0;
  font-size: clamp(42px, 6.6vw, 74px);
  line-height: 0.98;
  font-weight: 1000;
  letter-spacing: -0.04em;
  color: ${({ theme }) => theme.colors.text};
`;

const Accent = styled.span`
  color: #f59e0b;
`;

const AccentAlt = styled.span`
  color: #38bdf8;
`;

const Sub = styled.p`
  margin: 24px 0 0;
  max-width: 560px;
  font-size: 18px;
  line-height: 1.75;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const ProofRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 28px;
`;

const ProofPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.18);
  background: rgba(15, 23, 42, 0.46);
  color: ${({ theme }) => theme.colors.text};
  font-size: 14px;
  font-weight: 700;
`;

const CTAGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin-top: 34px;
`;

const LegalRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 18px;
`;

const LegalLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  text-decoration: none;
  color: ${({ theme }) => theme.colors.textSecondary};
  padding: 10px 14px;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.18);
  background: rgba(15, 23, 42, 0.42);
  font-size: 13px;
  font-weight: 800;

  &:hover {
    color: ${({ theme }) => theme.colors.text};
    border-color: rgba(56, 189, 248, 0.34);
  }
`;

const PrimaryCTA = styled(motion.button)`
  min-height: 56px;
  padding: 16px 24px;
  border-radius: 18px;
  border: none;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  color: #04111f;
  font-size: 16px;
  font-weight: 900;
  background: linear-gradient(135deg, #f59e0b, #facc15);
  box-shadow: 0 22px 48px rgba(245, 158, 11, 0.3);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 28px 60px rgba(245, 158, 11, 0.4);
  }
`;

const SecondaryCTA = styled(motion.button)`
  min-height: 56px;
  padding: 16px 22px;
  border-radius: 18px;
  border: 1px solid rgba(148, 163, 184, 0.22);
  display: inline-flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.text};
  font-size: 15px;
  font-weight: 800;
  background: rgba(15, 23, 42, 0.5);
  backdrop-filter: blur(12px);
  transition: transform 0.2s ease, border-color 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    border-color: rgba(56, 189, 248, 0.4);
  }
`;

const Meta = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
  margin-top: 36px;

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

const MetaCard = styled(motion.div)`
  padding: 18px 18px 16px;
  border-radius: 22px;
  border: 1px solid rgba(148, 163, 184, 0.16);
  background: rgba(15, 23, 42, 0.44);
  backdrop-filter: blur(12px);
`;

const MetaValue = styled.div`
  font-size: 28px;
  font-weight: 1000;
  color: ${({ theme }) => theme.colors.text};
`;

const MetaLabel = styled.div`
  margin-top: 8px;
  font-size: 13px;
  line-height: 1.55;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const Right = styled.div`
  position: relative;
`;

const ProductShell = styled(motion.div)`
  position: relative;
  padding: 20px;
  border-radius: 32px;
  border: 1px solid rgba(148, 163, 184, 0.18);
  background: linear-gradient(
      180deg,
      rgba(11, 18, 32, 0.92),
      rgba(4, 9, 18, 0.94)
    ),
    rgba(15, 23, 42, 0.74);
  box-shadow: 0 36px 100px rgba(2, 6, 23, 0.58);
`;

const ProductGlow = styled.div`
  position: absolute;
  inset: -18% -10% auto;
  height: 220px;
  background: radial-gradient(
    circle,
    rgba(56, 189, 248, 0.22),
    transparent 68%
  );
  pointer-events: none;
  filter: blur(8px);
`;

const ProductHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 18px;
`;

const ProductTitle = styled.div`
  color: #f8fafc;
  font-size: 18px;
  font-weight: 900;
`;

const ProductSub = styled.div`
  margin-top: 4px;
  color: #94a3b8;
  font-size: 13px;
`;

const LiveBadge = styled.div`
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(34, 197, 94, 0.14);
  color: #86efac;
  font-size: 12px;
  font-weight: 900;
  border: 1px solid rgba(34, 197, 94, 0.24);
`;

const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: 1.1fr 0.9fr;
  gap: 16px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const Panel = styled.div`
  padding: 18px;
  border-radius: 24px;
  background: rgba(15, 23, 42, 0.7);
  border: 1px solid rgba(148, 163, 184, 0.12);
`;

const PanelLabel = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #cbd5e1;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

const RoadmapStack = styled.div`
  display: grid;
  gap: 14px;
  margin-top: 16px;
`;

const RoadmapCard = styled(motion.div)`
  padding: 16px;
  border-radius: 20px;
  border: 1px solid
    ${({ active }) =>
      active ? "rgba(245, 158, 11, 0.4)" : "rgba(148, 163, 184, 0.12)"};
  background: ${({ active }) =>
    active
      ? "linear-gradient(135deg, rgba(245, 158, 11, 0.14), rgba(56, 189, 248, 0.08))"
      : "rgba(2, 6, 23, 0.45)"};
`;

const RoadmapRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const RoadmapHeading = styled.div`
  color: #f8fafc;
  font-size: 15px;
  font-weight: 800;
`;

const RoadmapText = styled.div`
  margin-top: 6px;
  color: #94a3b8;
  font-size: 13px;
  line-height: 1.55;
`;

const Status = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 999px;
  background: ${({ tone }) =>
    tone === "today"
      ? "rgba(245, 158, 11, 0.14)"
      : tone === "done"
      ? "rgba(34, 197, 94, 0.14)"
      : "rgba(56, 189, 248, 0.12)"};
  color: ${({ tone }) =>
    tone === "today" ? "#fcd34d" : tone === "done" ? "#86efac" : "#7dd3fc"};
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.04em;
`;

const MentorCard = styled(motion.div)`
  margin-top: 16px;
  padding: 16px;
  border-radius: 20px;
  background: linear-gradient(
    135deg,
    rgba(59, 130, 246, 0.18),
    rgba(99, 102, 241, 0.1)
  );
  border: 1px solid rgba(99, 102, 241, 0.22);
`;

const MentorTitle = styled.div`
  color: #f8fafc;
  font-size: 15px;
  font-weight: 800;
`;

const MentorText = styled.div`
  margin-top: 8px;
  color: #cbd5e1;
  font-size: 13px;
  line-height: 1.6;
`;

const InsightCard = styled(motion.div)`
  padding: 18px;
  border-radius: 24px;
  background: linear-gradient(
    180deg,
    rgba(8, 15, 28, 0.94),
    rgba(4, 9, 18, 0.94)
  );
  border: 1px solid rgba(148, 163, 184, 0.12);
`;

const InsightBlock = styled(motion.div)`
  padding: 14px 0;
  border-bottom: 1px solid rgba(148, 163, 184, 0.1);

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
`;

const InsightValue = styled.div`
  color: #f8fafc;
  font-size: 28px;
  font-weight: 1000;
`;

const InsightLabel = styled.div`
  margin-top: 6px;
  color: #94a3b8;
  font-size: 13px;
  line-height: 1.5;
`;

const PROOF = [
  { icon: Target, text: "Career path clarity" },
  { icon: CalendarCheck2, text: "Daily action plan" },
  { icon: Bot, text: "AI mentor support" },
];

const ROADMAP_PREVIEW = [
  {
    title: "Phase 1: Foundations",
    text: "Know what to study first so you stop hopping between tutorials.",
    status: "done",
    badge: "Completed",
  },
  {
    title: "Week 4: React State + APIs",
    text: "Build one focused project instead of collecting half-finished courses.",
    status: "today",
    badge: "Today",
  },
  {
    title: "Day 23: Product Catalog UI",
    text: "Learn one concept, build one feature, ship one real proof-of-work step.",
    status: "next",
    badge: "Next up",
  },
];

const staggerChildren = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.08,
    },
  },
};

const fadeUp = {
  hidden: { opacity: 0, y: 26 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
  },
};

export default function HeroSection() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isPro, setIsPro] = useState(false);
  const [hasRoadmap, setHasRoadmap] = useState(false);

  useEffect(() => {
    if (!user) return;

    const unsubUser = onSnapshot(doc(db, "users", user.uid), (snap) => {
      if (snap.exists()) {
        setIsPro(snap.data().plan === "PRO");
      }
    });

    const unsubRoadmap = onSnapshot(doc(db, "roadmaps", user.uid), (snap) => {
      setHasRoadmap(snap.exists());
    });

    return () => {
      unsubUser();
      unsubRoadmap();
    };
  }, [user]);

  return (
    <Section>
      <Container>
        <HeroGrid>
          <Left>
            <Eyebrow>
              <Sparkles size={16} />
              Learning, product development, and interview prep with one consistency system
            </Eyebrow>

            <Heading>
              Stop <Accent>guessing</Accent> your next move.
              <br />
              Start <AccentAlt>building</AccentAlt> with a real roadmap.
            </Heading>

            <Sub>
              `skilll.dev` gives you one safe agenda: learn better, build real
              products, and prepare for interviews with a roadmap and
              consistency system that keeps daily execution alive.
            </Sub>

            <ProofRow>
              {PROOF.map((item) => {
                const Icon = item.icon;
                return (
                  <ProofPill key={item.text}>
                    <Icon size={15} />
                    {item.text}
                  </ProofPill>
                );
              })}
            </ProofRow>

            <CTAGroup>
              {user && hasRoadmap ? (
                <PrimaryCTA
                  whileHover={{ y: -3, scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  transition={{ type: "spring", stiffness: 240, damping: 16 }}
                  onClick={() => navigate("/my-roadmap")}
                >
                  Open My Roadmap
                  <ArrowRight size={18} />
                </PrimaryCTA>
              ) : (
                <PrimaryCTA
                  whileHover={{ y: -3, scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  transition={{ type: "spring", stiffness: 240, damping: 16 }}
                  onClick={() => navigate("/career-clarity-home")}
                >
                  Create My Free Roadmap
                  <ArrowRight size={18} />
                </PrimaryCTA>
              )}

              {user && !isPro ? (
                <SecondaryCTA
                  whileHover={{ y: -3, scale: 1.01 }}
                  whileTap={{ scale: 0.985 }}
                  transition={{ type: "spring", stiffness: 240, damping: 16 }}
                  onClick={() => navigate("/pricing")}
                >
                  <Flame size={16} />
                  Unlock consistency system
                </SecondaryCTA>
              ) : (
                <SecondaryCTA
                  whileHover={{ y: -3, scale: 1.01 }}
                  whileTap={{ scale: 0.985 }}
                  transition={{ type: "spring", stiffness: 240, damping: 16 }}
                  onClick={() =>
                    document
                      .getElementById("how-it-works-section")
                      ?.scrollIntoView({
                        behavior: "smooth",
                      })
                  }
                >
                  <Map size={16} />
                  See how it works
                </SecondaryCTA>
              )}
            </CTAGroup>

            <LegalRow>
              <LegalLink to="/privacy-policy">Privacy Policy</LegalLink>
              <LegalLink to="/terms-of-service">Terms of Service</LegalLink>
            </LegalRow>

            <Meta>
              <MetaCard
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.18, duration: 0.6 }}
                whileHover={{ y: -4 }}
              >
                <MetaValue>Phase -> Week -> Day</MetaValue>
                <MetaLabel>
                  Clear structure so users always know what to do next.
                </MetaLabel>
              </MetaCard>
              <MetaCard
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.28, duration: 0.6 }}
                whileHover={{ y: -4 }}
              >
                <MetaValue>Projects, not theory</MetaValue>
                <MetaLabel>
                  Learn by shipping work that strengthens your resume.
                </MetaLabel>
              </MetaCard>
              <MetaCard
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.38, duration: 0.6 }}
                whileHover={{ y: -4 }}
              >
                <MetaValue>Consistency support</MetaValue>
                <MetaLabel>
                  Streaks, reminders, and mentor help keep momentum alive.
                </MetaLabel>
              </MetaCard>
            </Meta>
          </Left>

          <Right>
            <ProductShell
              initial={{ opacity: 0, y: 22 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, ease: "easeOut" }}
            >
              <ProductGlow />
              <ProductHeader>
                <div>
                  <ProductTitle>Your Product Roadmap System</ProductTitle>
                  <ProductSub>
                    Direction, execution, and accountability in one place
                  </ProductSub>
                </div>
                <LiveBadge>Daily flow</LiveBadge>
              </ProductHeader>

              <ProductGrid>
                <div>
                  <Panel>
                    <PanelLabel>
                      <Map size={14} />
                      Roadmap Preview
                    </PanelLabel>

                    <RoadmapStack as={motion.div} variants={staggerChildren} initial="hidden" animate="show">
                      {ROADMAP_PREVIEW.map((item) => (
                        <RoadmapCard
                          key={item.title}
                          active={item.status === "today"}
                          variants={fadeUp}
                          whileHover={{
                            y: -4,
                            scale: 1.01,
                            boxShadow: "0 20px 36px rgba(2, 6, 23, 0.26)",
                          }}
                        >
                          <RoadmapRow>
                            <RoadmapHeading>{item.title}</RoadmapHeading>
                            <Status tone={item.status}>
                              <CheckCircle2 size={12} />
                              {item.badge}
                            </Status>
                          </RoadmapRow>
                          <RoadmapText>{item.text}</RoadmapText>
                        </RoadmapCard>
                      ))}
                    </RoadmapStack>
                  </Panel>

                  <MentorCard
                    initial={{ opacity: 0, y: 28 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.44, duration: 0.7 }}
                    whileHover={{ y: -4, scale: 1.01 }}
                  >
                    <MentorTitle>AI Mentor</MentorTitle>
                    <MentorText>
                      "You do not need another course right now. Finish today’s
                      feature, then ask me where your state management is weak."
                    </MentorText>
                  </MentorCard>
                </div>

                <InsightCard
                  initial={{ opacity: 0, x: 24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.28, duration: 0.7 }}
                  whileHover={{ y: -4 }}
                >
                  <PanelLabel>
                    <Target size={14} />
                    Why It Converts
                  </PanelLabel>

                  <InsightBlock
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.42, duration: 0.55 }}
                  >
                    <InsightValue>1 clear path</InsightValue>
                    <InsightLabel>
                      Users stop switching stacks and start following one
                      realistic direction.
                    </InsightLabel>
                  </InsightBlock>

                  <InsightBlock
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.52, duration: 0.55 }}
                  >
                    <InsightValue>1 task per day</InsightValue>
                    <InsightLabel>
                      Small, visible progress beats binge-learning and burnout.
                    </InsightLabel>
                  </InsightBlock>

                  <InsightBlock
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.62, duration: 0.55 }}
                  >
                    <InsightValue>Real outcomes</InsightValue>
                    <InsightLabel>
                      Better projects, better proof of work, better career
                      confidence.
                    </InsightLabel>
                  </InsightBlock>
                </InsightCard>
              </ProductGrid>
            </ProductShell>
          </Right>
        </HeroGrid>
      </Container>
    </Section>
  );
}
